/**
 * NOTE: everything here is a client-side ESTIMATE from course progress. The source of truth is the server:
 * stored XP (GET /api/gamification/xp/:userId), database-counted streaks (GET /streaks/:userId) and stored
 * achievements (GET /achievements/summary). Use this module only as a fallback when those requests fail.
 *
 * Backward Compatible Analytics Calculator
 * Calculates XP, achievements, and analytics from existing user progress data
 * This ensures enhanced learning features work even without dedicated analytics APIs
 */

// XP calculation constants
const XP_CONSTANTS = {
  COURSE_COMPLETION: 1000,
  COURSE_START: 50,
  PAGE_COMPLETION: 10,
  TIME_BONUS_PER_HOUR: 25,
  STREAK_MULTIPLIER: 1.5,
  ACHIEVEMENT_BONUS: 200
};

// Achievement definitions
const ACHIEVEMENTS = {
  FIRST_COURSE: {
    id: 'first_course',
    name: 'Getting Started',
    description: 'Complete your first course',
    icon: '🎓',
    xp: 100,
    condition: (stats) => stats.completedCourses >= 1
  },
  COURSE_MASTER: {
    id: 'course_master',
    name: 'Course Master',
    description: 'Complete 5 courses',
    icon: '🏆',
    xp: 500,
    condition: (stats) => stats.completedCourses >= 5
  },
  SPEED_LEARNER: {
    id: 'speed_learner',
    name: 'Speed Learner',
    description: 'Complete a course in under 2 hours',
    icon: '⚡',
    xp: 300,
    condition: (stats) => stats.fastestCompletion <= 120 // 2 hours in minutes
  },
  DEDICATED_STUDENT: {
    id: 'dedicated_student',
    name: 'Dedicated Student',
    description: 'Study for 10+ hours total',
    icon: '📚',
    xp: 400,
    condition: (stats) => stats.totalTimeSpent >= 600 // 10 hours in minutes
  },
  CONSISTENT_LEARNER: {
    id: 'consistent_learner',
    name: 'Consistent Learner',
    description: 'Study for 7 consecutive days',
    icon: '🔥',
    xp: 350,
    condition: (stats) => stats.currentStreak >= 7
  },
  PERFECTIONIST: {
    id: 'perfectionist',
    name: 'Perfectionist',
    description: 'Complete 3 courses with 100% progress',
    icon: '💎',
    xp: 600,
    condition: (stats) => stats.perfectCompletions >= 3
  }
};

/**
 * Calculate XP from course progress data
 */
export function calculateXP(courseProgress) {
  let totalXP = 0;
  let fastestCompletion = Infinity;
  let perfectCompletions = 0;

  courseProgress.forEach(course => {
    // Base XP for starting a course
    if (course.progress > 0) {
      totalXP += XP_CONSTANTS.COURSE_START;
    }

    // XP for course completion
    if (course.status === 'completed') {
      totalXP += XP_CONSTANTS.COURSE_COMPLETION;
      
      // Track fastest completion
      if (course.timeSpent < fastestCompletion) {
        fastestCompletion = course.timeSpent;
      }
      
      // Track perfect completions
      if (course.progress === 100) {
        perfectCompletions++;
      }
    }

    // XP for pages completed
    totalXP += (course.completedPages || 0) * XP_CONSTANTS.PAGE_COMPLETION;

    // Time bonus (XP for time spent)
    const hoursSpent = (course.timeSpent || 0) / 60;
    totalXP += Math.floor(hoursSpent * XP_CONSTANTS.TIME_BONUS_PER_HOUR);
  });

  return {
    totalXP: Math.floor(totalXP),
    level: calculateLevel(totalXP),
    fastestCompletion: fastestCompletion === Infinity ? 0 : fastestCompletion,
    perfectCompletions
  };
}

/**
 * Calculate user level from total XP
 */
function calculateLevel(totalXP) {
  // Level formula: Level = floor(sqrt(XP / 100)) + 1
  return Math.floor(Math.sqrt(totalXP / 100)) + 1;
}

/**
 * Calculate achievements from course progress and XP data
 */
export function calculateAchievements(courseProgress, xpData) {
  const stats = {
    completedCourses: courseProgress.filter(c => c.status === 'completed').length,
    totalCourses: courseProgress.length,
    totalTimeSpent: courseProgress.reduce((total, c) => total + (c.timeSpent || 0), 0),
    fastestCompletion: xpData.fastestCompletion,
    perfectCompletions: xpData.perfectCompletions,
    currentStreak: calculateStreak(courseProgress)
  };

  const earnedAchievements = [];

  Object.values(ACHIEVEMENTS).forEach(achievement => {
    if (achievement.condition(stats)) {
      earnedAchievements.push({
        ...achievement,
        earnedAt: new Date().toISOString(),
        isNew: false // We don't track new achievements in backward compatibility mode
      });
    }
  });

  return earnedAchievements;
}

/**
 * Calculate current streak from course progress
 */
function calculateStreak(courseProgress) {
  // Simple streak calculation based on recent activity
  // In a real implementation, this would check daily activity
  const recentActivity = courseProgress.filter(course => {
    if (!course.lastAccessed) return false;
    const lastAccessed = new Date(course.lastAccessed);
    const daysSinceAccess = (Date.now() - lastAccessed.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceAccess <= 1; // Active in last 24 hours
  });

  return recentActivity.length > 0 ? 1 : 0; // Simplified streak calculation
}

/**
 * Calculate learning analytics from course progress
 */
export function calculateLearningAnalytics(courseProgress, userId) {
  const completedCourses = courseProgress.filter(c => c.status === 'completed');
  const inProgressCourses = courseProgress.filter(c => c.status === 'in_progress');
  
  // Calculate average progress
  const averageProgress = courseProgress.length > 0 
    ? courseProgress.reduce((sum, c) => sum + c.progress, 0) / courseProgress.length 
    : 0;

  // Calculate engagement score based on time spent and completion rate
  const totalTimeSpent = courseProgress.reduce((total, c) => total + (c.timeSpent || 0), 0);
  const completionRate = courseProgress.length > 0 ? completedCourses.length / courseProgress.length : 0;
  const engagementScore = Math.min(100, (completionRate * 50) + (totalTimeSpent / 60) * 2);

  return {
    analytics: [{
      id: `analytics_${userId}_${Date.now()}`,
      user_id: userId,
      average_score: averageProgress,
      engagement_score: Math.round(engagementScore),
      completion_rate: Math.round(completionRate * 100),
      total_time_spent: totalTimeSpent,
      courses_completed: completedCourses.length,
      courses_in_progress: inProgressCourses.length,
      created_at: new Date().toISOString()
    }],
    summary: {
      totalCourses: courseProgress.length,
      completedCourses: completedCourses.length,
      averageProgress: Math.round(averageProgress),
      totalTimeSpent,
      engagementScore: Math.round(engagementScore)
    }
  };
}

/**
 * Generate personalized recommendations based on progress
 */
export function generatePersonalizedRecommendations(courseProgress, achievements) {
  const recommendations = [];
  const completedCourses = courseProgress.filter(c => c.status === 'completed');
  const inProgressCourses = courseProgress.filter(c => c.status === 'in_progress');
  const notStartedCourses = courseProgress.filter(c => c.status === 'not_started');

  // Recommendation 1: Complete in-progress courses
  if (inProgressCourses.length > 0) {
    inProgressCourses.forEach(course => {
      recommendations.push({
        id: `continue_${course.id}`,
        type: 'continue_course',
        title: `Continue "${course.title}"`,
        description: `You're ${course.progress}% complete. Keep going to finish this course!`,
        priority: 'high',
        action: `Continue Course`,
        courseId: course.id
      });
    });
  }

  // Recommendation 2: Start new courses
  if (notStartedCourses.length > 0 && completedCourses.length > 0) {
    notStartedCourses.slice(0, 2).forEach(course => {
      recommendations.push({
        id: `start_${course.id}`,
        type: 'start_course',
        title: `Start "${course.title}"`,
        description: `Ready for your next learning adventure? This course looks interesting!`,
        priority: 'medium',
        action: `Start Course`,
        courseId: course.id
      });
    });
  }

  // Recommendation 3: Achievement-based recommendations
  const hasFirstCourse = achievements.some(a => a.id === 'first_course');
  if (!hasFirstCourse && courseProgress.length > 0) {
    const firstCourse = courseProgress[0];
    recommendations.push({
      id: 'first_completion',
      type: 'achievement',
      title: 'Complete Your First Course',
      description: `Finish "${firstCourse.title}" to earn your first achievement!`,
      priority: 'high',
      action: `Complete Course`,
      courseId: firstCourse.id
    });
  }

  // Recommendation 4: Time-based recommendations
  const totalTimeSpent = courseProgress.reduce((total, c) => total + (c.timeSpent || 0), 0);
  if (totalTimeSpent < 60) { // Less than 1 hour total
    recommendations.push({
      id: 'study_more',
      type: 'study_habit',
      title: 'Build Your Study Habit',
      description: 'Try to study for at least 30 minutes today to build a consistent learning routine.',
      priority: 'medium',
      action: `Start Studying`,
      courseId: null
    });
  }

  return recommendations.slice(0, 4); // Limit to 4 recommendations
}

/**
 * Calculate adaptive learning settings based on progress patterns
 */
export function calculateAdaptiveSettings(courseProgress, xpData) {
  const totalTimeSpent = courseProgress.reduce((total, c) => total + (c.timeSpent || 0), 0);
  const averageProgress = courseProgress.length > 0 
    ? courseProgress.reduce((sum, c) => sum + c.progress, 0) / courseProgress.length 
    : 0;

  // Determine learning pace based on completion rate and time spent
  let learningPace = 'moderate';
  if (averageProgress > 80) {
    learningPace = 'fast';
  } else if (averageProgress < 30) {
    learningPace = 'slow';
  }

  // Determine content preference based on time spent per course
  const avgTimePerCourse = courseProgress.length > 0 ? totalTimeSpent / courseProgress.length : 0;
  let contentPreference = 'balanced';
  if (avgTimePerCourse > 120) { // More than 2 hours per course
    contentPreference = 'detailed';
  } else if (avgTimePerCourse < 30) { // Less than 30 minutes per course
    contentPreference = 'concise';
  }

  // Calculate mastery threshold based on completion patterns
  const completionRate = courseProgress.length > 0 
    ? courseProgress.filter(c => c.status === 'completed').length / courseProgress.length 
    : 0;
  const masteryThreshold = Math.max(70, Math.min(95, 70 + (completionRate * 25)));

  return {
    learning_pace: learningPace,
    content_preference: contentPreference,
    mastery_threshold: Math.round(masteryThreshold),
    difficulty_adjustment: 'auto',
    feedback_frequency: 'moderate',
    reminder_settings: {
      daily_reminder: true,
      weekly_summary: true,
      achievement_notifications: true
    }
  };
}

/**
 * Main function to calculate all backward-compatible analytics
 */
export function calculateBackwardCompatibleAnalytics(courseProgress, userId) {
  const xpData = calculateXP(courseProgress);
  const achievements = calculateAchievements(courseProgress, xpData);
  const learningAnalytics = calculateLearningAnalytics(courseProgress, userId);
  const personalizedRecommendations = generatePersonalizedRecommendations(courseProgress, achievements);
  const adaptiveSettings = calculateAdaptiveSettings(courseProgress, xpData);

  const xpToNextLevel = calculateXPToNextLevel(xpData.totalXP, xpData.level);
  const currentLevelXP = Math.pow(xpData.level - 1, 2) * 100; // XP needed for current level
  const xpInCurrentLevel = Math.max(0, xpData.totalXP - currentLevelXP);
  
  return {
    userXP: {
      total_xp: xpData.totalXP,
      level_number: xpData.level,
      level_name: `Level ${xpData.level}`,
      xp_to_next_level: xpToNextLevel,
      xp_in_current_level: xpInCurrentLevel
    },
    achievements: {
      achievements: achievements,
      total_achievements: achievements.length,
      recent_achievements: achievements.slice(-3) // Last 3 achievements
    },
    learningAnalytics,
    personalizedRecommendations,
    adaptiveSettings,
    streaks: [{
      streak_type: 'daily_activity',
      current_count: calculateStreak(courseProgress),
      longest_count: calculateStreak(courseProgress), // Simplified
      last_activity: new Date().toISOString()
    }]
  };
}

/**
 * Calculate XP needed for next level
 */
function calculateXPToNextLevel(currentXP, currentLevel) {
  const nextLevelXP = Math.pow(currentLevel, 2) * 100;
  return Math.max(0, nextLevelXP - currentXP);
}

/**
 * Fetch and compute backward-compatible gamification snapshot from Supabase.
 * Shared by Navbar + Marketplace to avoid duplicate query logic.
 *
 * @param {object} [options]
 * @param {boolean} [options.skipUnstartedTotals=false] - Only count pages for courses the learner has
 *   progress rows for. Unstarted courses stay at 0% either way, so XP and achievements are unchanged,
 *   but their module/submodule/page tree is no longer fetched (their `totalPages` is reported as 0).
 */
export async function fetchBackwardCompatibleGamificationSnapshot(userId, supabaseClient, options = {}) {
  const { skipUnstartedTotals = false } = options;
  if (!userId || !supabaseClient) {
    return {
      userXP: { total_xp: 0, level_number: 1, xp_in_current_level: 0, xp_to_next_level: 100 },
      achievements: { achievements: [], total_achievements: 0, recent_achievements: [] },
      streaks: [],
      courseProgress: [],
    };
  }

  const { data: enrolledCourses } = await supabaseClient
    .from('enrolled')
    .select('course_id')
    .eq('user_id', userId)
    .eq('status', 'active');

  if (!enrolledCourses?.length) {
    return {
      userXP: { total_xp: 0, level_number: 1, xp_in_current_level: 0, xp_to_next_level: 100 },
      achievements: { achievements: [], total_achievements: 0, recent_achievements: [] },
      streaks: [],
      courseProgress: [],
    };
  }

  const enrolledIds = enrolledCourses.map((row) => row.course_id).filter(Boolean);
  const [{ data: courses }, { data: progressData }] = await Promise.all([
    supabaseClient.from('dynamic_courses').select('id, title, description, thumbnail_url').in('id', enrolledIds),
    supabaseClient.from('user_progress').select('course_id, page_id, status, time_spent, last_accessed').eq('user_id', userId).in('course_id', enrolledIds),
  ]);

  const validCourses = (courses || []).filter((course) => enrolledIds.includes(course.id));
  if (!validCourses.length) {
    return {
      userXP: { total_xp: 0, level_number: 1, xp_in_current_level: 0, xp_to_next_level: 100 },
      achievements: { achievements: [], total_achievements: 0, recent_achievements: [] },
      streaks: [],
      courseProgress: [],
    };
  }

  const progressMap = {};
  (progressData || []).forEach((entry) => {
    if (!progressMap[entry.course_id]) {
      progressMap[entry.course_id] = {
        completedPages: 0,
        recordedPages: 0,
        timeSpent: 0,
        lastAccessed: entry.last_accessed || null,
      };
    }
    const stats = progressMap[entry.course_id];
    if (entry.page_id) stats.recordedPages++;
    if (entry.page_id && entry.status === 'completed') stats.completedPages++;
    if (entry.time_spent) {
      const match = String(entry.time_spent).match(/(\d+):(\d+):(\d+)/);
      if (match) stats.timeSpent += parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
    }
    if (entry.last_accessed && (!stats.lastAccessed || new Date(entry.last_accessed) > new Date(stats.lastAccessed))) {
      stats.lastAccessed = entry.last_accessed;
    }
  });

  const validIds = validCourses.map((course) => course.id);
  const totalsIds = skipUnstartedTotals ? validIds.filter((id) => progressMap[id]) : validIds;
  const { data: modules } = totalsIds.length
    ? await supabaseClient.from('modules').select('id, course_id').in('course_id', totalsIds)
    : { data: [] };
  const moduleIds = (modules || []).map((module) => module.id);
  const { data: submodules } = moduleIds.length
    ? await supabaseClient.from('submodules').select('id, module_id').in('module_id', moduleIds)
    : { data: [] };
  const submoduleIds = (submodules || []).map((submodule) => submodule.id);
  const { data: pages } = submoduleIds.length
    ? await supabaseClient.from('submodule_pages').select('id, submodule_id').in('submodule_id', submoduleIds)
    : { data: [] };

  const modulesByCourse = {};
  (modules || []).forEach((module) => {
    if (!modulesByCourse[module.course_id]) modulesByCourse[module.course_id] = [];
    modulesByCourse[module.course_id].push(module.id);
  });

  const submodulesByModule = {};
  (submodules || []).forEach((submodule) => {
    if (!submodulesByModule[submodule.module_id]) submodulesByModule[submodule.module_id] = [];
    submodulesByModule[submodule.module_id].push(submodule.id);
  });

  const pageCountBySubmodule = {};
  (pages || []).forEach((page) => {
    pageCountBySubmodule[page.submodule_id] = (pageCountBySubmodule[page.submodule_id] || 0) + 1;
  });

  const getTotalPages = (courseId) => {
    let total = 0;
    (modulesByCourse[courseId] || []).forEach((moduleId) => {
      (submodulesByModule[moduleId] || []).forEach((submoduleId) => {
        total += pageCountBySubmodule[submoduleId] || 0;
      });
    });
    return total;
  };

  const courseProgress = validCourses.map((course) => {
    const stats = progressMap[course.id] || { completedPages: 0, timeSpent: 0, lastAccessed: null };
    const totalPages = getTotalPages(course.id);
    const progress = totalPages > 0 ? Math.round((stats.completedPages / totalPages) * 100) : 0;
    return {
      id: course.id,
      title: course.title,
      description: course.description,
      thumbnail_url: course.thumbnail_url,
      progress,
      status: progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started',
      timeSpent: stats.timeSpent,
      lastAccessed: stats.lastAccessed,
      totalPages,
      completedPages: stats.completedPages,
      // Pages the learner has a progress row for. Some courses have no module tree (totalPages 0), so
      // clients can fall back to completed / recorded, the same way the dashboard stats do.
      recordedPages: stats.recordedPages || 0,
    };
  });

  return {
    ...calculateBackwardCompatibleAnalytics(courseProgress, userId),
    courseProgress,
  };
}
